import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Badge,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  useFetchArchiveTypesQuery,
  useFetchArchivesQuery,
  useRestoreArchivesMutation,
} from "features/archives/archivesSlice";
import { useFetchAnneeScolairesQuery } from "features/anneeScolaire/anneeScolaireSlice";
import { useSelector } from "react-redux";
import { RootState } from "app/store";

const COLUMN_LABELS: Record<string, string> = {
  nom: "Nom", prenom: "Prénom", classe: "Classe", genre: "Genre",
  date_naissance: "Date de naissance", status: "Statut", eleve: "Élève",
  matiere: "Matière", trimestre: "Trimestre", note: "Note", type: "Type",
  date: "Date", motif: "Motif", description: "Description",
  observation: "Observation", montant: "Montant", date_paiement: "Date paiement",
  type_paiement: "Type paiement", titre: "Titre", duree: "Durée",
  moyenne_generale: "Moyenne générale", date_inscription: "Date inscription",
  jour: "Jour", heure_debut: "Heure début", heure_fin: "Heure fin",
  heure_sortie: "Heure sortie", record_status: "Statut",
};

const formatCellValue = (item: any, col: string): string => {
  const val = item[col];
  if (val === undefined || val === null) return "-";
  if (col === "classe") return val?.nom_classe || val?.label || String(val);
  if (col === "eleve") {
    if (val?.nom && val?.prenom) return `${val.prenom} ${val.nom}`;
    if (val?.prenom) return val.prenom;
    return String(val);
  }
  if (col === "matiere") return val?.nom_matiere || val?.label || String(val);
  if (["date", "date_naissance", "date_paiement", "date_inscription"].includes(col)) {
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString("fr-FR");
  }
  return String(val);
};

const StatusBadge = ({ status }: { status: string }) => {
  if (status === "inactive") return <Badge bg="secondary">Archivé</Badge>;
  if (status === "deleted") return <Badge bg="danger">Supprimé</Badge>;
  if (status === "active") return <Badge bg="success">Actif</Badge>;
  return <Badge bg="light" text="dark">{status || "-"}</Badge>;
};

interface Props {
  initialAnnee?: string;
}

const ArchivesTab: React.FC<Props> = ({ initialAnnee }) => {
  const { data: archiveTypes = [] } = useFetchArchiveTypesQuery();
  const { data: annees = [] } = useFetchAnneeScolairesQuery();
  const [restoreArchives] = useRestoreArchivesMutation();
  const token = useSelector((state: RootState) => state.auth?.central?.api_token);

  const [selectedType, setSelectedType] = useState("");
  const [selectedAnnee, setSelectedAnnee] = useState(initialAnnee || "");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const sortedAnnees = [...annees].sort((a, b) => {
    if (a.isCurrent) return 1;
    if (b.isCurrent) return -1;
    return 0;
  });

  const canQuery = selectedType && selectedAnnee;

  const { data: archivesData, isLoading, isFetching } = useFetchArchivesQuery(
    { type: selectedType, annee_scolaire: selectedAnnee, search: search || undefined, page: currentPage, limit: 50 },
    { skip: !canQuery }
  );

  const currentTypeConfig = useMemo(
    () => archiveTypes.find((t) => t.value === selectedType),
    [archiveTypes, selectedType]
  );
  const columns = currentTypeConfig?.columns || [];

  const handleTypeChange = (val: string) => { setSelectedType(val); setCurrentPage(1); setSelectedIds([]); };
  const handleAnneeChange = (val: string) => { setSelectedAnnee(val); setCurrentPage(1); setSelectedIds([]); };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (!archivesData?.data) return;
    const allIds = archivesData.data.map((d: any) => d._id);
    setSelectedIds(selectedIds.length === allIds.length ? [] : allIds);
  };

  const handleRestore = async (ids?: string[]) => {
    const restoreIds = ids || selectedIds;
    if (restoreIds.length === 0) return;
    const currentYear = annees.find((a) => a.isCurrent);
    const result = await Swal.fire({
      title: `Restaurer ${restoreIds.length} élément(s) ?`,
      html: `Ces éléments seront remis en statut actif et transférés vers l'année <strong>${currentYear?.label || "courante"}</strong>.`,
      icon: "question", showCancelButton: true, confirmButtonText: "Restaurer", cancelButtonText: "Annuler",
    });
    if (result.isConfirmed) {
      try {
        const res = await restoreArchives({ type: selectedType, ids: restoreIds }).unwrap();
        Swal.fire("Succès", res.message, "success");
        setSelectedIds([]);
      } catch (err: any) {
        Swal.fire("Erreur", err?.data?.message || "Erreur", "error");
      }
    }
  };

  const handleExportExcel = async () => {
    if (!canQuery) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ type: selectedType, annee_scolaire: selectedAnnee });
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/archives/export?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) { const err = await response.json(); throw new Error(err.message || "Erreur export"); }
      const blob = await response.blob();
      const typeLabel = currentTypeConfig?.label || selectedType;
      const filename = `Archives_${typeLabel}_${selectedAnnee}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      Swal.fire({ icon: "success", title: "Export terminé", html: `<p>Fichier <strong>${filename}</strong> téléchargé.</p>`, timer: 3000 });
    } catch (err: any) {
      Swal.fire("Erreur", err.message || "Erreur lors de l'export", "error");
    } finally { setExporting(false); }
  };

  return (
    <Card>
      <Card.Header>
        <h5 className="card-title mb-0">
          <i className="ph ph-archive me-2"></i> Archives
        </h5>
      </Card.Header>
      <Card.Body>
        {/* Filters */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <Form.Label className="fw-semibold">Année scolaire</Form.Label>
            <Form.Select value={selectedAnnee} onChange={(e) => handleAnneeChange(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {sortedAnnees.map((a) => (
                <option key={a._id} value={a.label}>{a.label} {a.isCurrent ? "(en cours)" : ""}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="fw-semibold">Type de données</Form.Label>
            <Form.Select value={selectedType} onChange={(e) => handleTypeChange(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {archiveTypes.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="fw-semibold">Recherche</Form.Label>
            <InputGroup>
              <Form.Control type="text" placeholder="Nom, prénom, titre..." value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} />
              {search && (
                <Button variant="outline-secondary" onClick={() => setSearch("")}>
                  <i className="ri-close-line"></i>
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col md={3} className="d-flex align-items-end gap-2">
            {canQuery && archivesData && archivesData.data.length > 0 && (
              <>
                <Button variant="success" size="sm" onClick={handleExportExcel} disabled={exporting}>
                  {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="ri-file-excel-2-line me-1"></i>}
                  {exporting ? "Export..." : "Exporter Excel"}
                </Button>
                {selectedIds.length > 0 && (
                  <Button variant="info" size="sm" onClick={() => handleRestore()}>
                    <i className="ri-refresh-line me-1"></i> Restaurer ({selectedIds.length})
                  </Button>
                )}
              </>
            )}
          </Col>
        </Row>

        {/* Results */}
        {!canQuery ? (
          <div className="text-center py-5">
            <i className="ph ph-archive text-muted" style={{ fontSize: "4rem" }}></i>
            <p className="text-muted mt-3">Sélectionnez une année scolaire et un type de données pour consulter les archives.</p>
          </div>
        ) : isLoading || isFetching ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2">Chargement des archives...</p>
          </div>
        ) : !archivesData || archivesData.data.length === 0 ? (
          <div className="text-center py-5">
            <i className="ph ph-folder-open text-muted" style={{ fontSize: "4rem" }}></i>
            <p className="text-muted mt-3">Aucune donnée trouvée pour cette période.</p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted"><strong>{archivesData.total}</strong> résultat(s) pour <strong>{selectedAnnee}</strong></span>
              <span className="text-muted small">Page {archivesData.page} / {archivesData.totalPages}</span>
            </div>
            <div className="table-responsive">
              <Table className="table-hover align-middle table-nowrap">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>
                      <Form.Check type="checkbox"
                        checked={archivesData.data.length > 0 && selectedIds.length === archivesData.data.length}
                        onChange={toggleSelectAll} />
                    </th>
                    {columns.map((col) => (<th key={col}>{COLUMN_LABELS[col] || col}</th>))}
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archivesData.data.map((item: any) => (
                    <tr key={item._id}>
                      <td><Form.Check type="checkbox" checked={selectedIds.includes(item._id)} onChange={() => toggleSelect(item._id)} /></td>
                      {columns.map((col) => (
                        <td key={col}>
                          {col === "status" || col === "record_status" ? <StatusBadge status={formatCellValue(item, col)} /> : formatCellValue(item, col)}
                        </td>
                      ))}
                      <td>
                        <Button variant="soft-info" size="sm" title="Restaurer" onClick={() => handleRestore([item._id])}>
                          <i className="ri-refresh-line"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {archivesData.totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3 gap-2">
                <Button variant="light" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                  <i className="ri-arrow-left-s-line"></i> Précédent
                </Button>
                {Array.from({ length: Math.min(archivesData.totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (archivesData.totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= archivesData.totalPages - 2) pageNum = archivesData.totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <Button key={pageNum} variant={currentPage === pageNum ? "primary" : "light"} size="sm" onClick={() => setCurrentPage(pageNum)}>
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="light" size="sm" disabled={currentPage === archivesData.totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                  Suivant <i className="ri-arrow-right-s-line"></i>
                </Button>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ArchivesTab;
